/**
 * useProgressStream — consume SSE progress events from a multi-step backend operation.
 *
 * Usage:
 *   const { trigger, currentStep, message, data, error, isLoading } = useProgressStream<MyResult>();
 *
 *   // Start the operation:
 *   trigger('/api/v1/master-data/contacts/organizations/enrich-domain', { domain: 'example.com' });
 *
 * The backend should return `text/event-stream` with JSON `data:` lines:
 *   { "step": "fetching", "message": "Fetching website..." }
 *   { "step": "done", "message": "Complete", "data": { ... } }
 */
import { useState, useCallback, useRef } from 'react';
import { getValidAccessToken } from '../api/axiosInstance';

export interface ProgressState<T = unknown> {
  /** Current step identifier (e.g. "fetching", "analyzing", "done", "error") */
  currentStep: string | null;
  /** Human-readable message for the current step */
  message: string | null;
  /** Final result data (set when step="done") */
  data: T | null;
  /** Error message if the operation failed */
  error: string | null;
  /** Whether the stream is currently active */
  isLoading: boolean;
}

export interface UseProgressStreamReturn<T = unknown> extends ProgressState<T> {
  /** Start the streaming operation. Body is sent as JSON POST. */
  trigger: (url: string, body?: Record<string, unknown>) => Promise<void>;
  /** Reset state to initial values */
  reset: () => void;
}

const INITIAL_STATE: ProgressState = {
  currentStep: null,
  message: null,
  data: null,
  error: null,
  isLoading: false,
};

export function useProgressStream<T = unknown>(): UseProgressStreamReturn<T> {
  const [state, setState] = useState<ProgressState<T>>(INITIAL_STATE as ProgressState<T>);
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setState(INITIAL_STATE as ProgressState<T>);
  }, []);

  const trigger = useCallback(async (url: string, body?: Record<string, unknown>) => {
    // Abort any in-flight request
    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setState({
      currentStep: null,
      message: null,
      data: null,
      error: null,
      isLoading: true,
    });

    try {
      const token = await getValidAccessToken();
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE lines
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;

          try {
            const event = JSON.parse(line.slice(6));

            if (event.step === 'error') {
              setState(prev => ({
                ...prev,
                currentStep: 'error',
                message: event.message,
                error: event.error || event.message,
                isLoading: false,
              }));
              return;
            }

            if (event.step === 'done') {
              setState(prev => ({
                ...prev,
                currentStep: 'done',
                message: event.message,
                data: event.data ?? prev.data,
                isLoading: false,
              }));
              return;
            }

            // Intermediate step
            setState(prev => ({
              ...prev,
              currentStep: event.step,
              message: event.message,
            }));
          } catch {
            // Skip malformed lines
          }
        }
      }

      // Stream ended without a done/error event
      setState(prev => {
        if (prev.isLoading) {
          return { ...prev, isLoading: false };
        }
        return prev;
      });
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      setState(prev => ({
        ...prev,
        currentStep: 'error',
        message: err instanceof Error ? err.message : 'Unknown error',
        error: err instanceof Error ? err.message : 'Unknown error',
        isLoading: false,
      }));
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
    }
  }, []);

  return { ...state, trigger, reset };
}
